import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { chatApi } from "apis";
import { SecureStorageKeys } from "constants/secureStorage";
import { ConversationListResponseDto } from "libs/cellera-sdk";
import { debounce } from "lodash";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Socket } from "socket.io-client";
import { ChatSubscribeMessage } from "types/socket";
import { sessionEvents } from "utils/apiInterceptor";
import {
  disconnectSocket,
  initChatSocket,
  stopSocketReconnection,
} from "utils/chatSocket";
import { secureStorage } from "utils/secureStorage";

type ChatSocketContextValue = {
  socket: Socket | null;
  isConnected: boolean;
};

export const ChatSocketContext = createContext<ChatSocketContextValue>({
  socket: null,
  isConnected: false,
});

export function ChatSocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  // Track joined rooms to avoid duplicate JOIN_CONVERSATION emissions
  const joinedRoomsRef = useRef<Set<string>>(new Set());
  // Prevent race conditions during fetch
  const isFetchingRef = useRef(false);

  // Function to join all conversation rooms
  const joinAllConversationRooms = useCallback(
    async (s: Socket) => {
      // Prevent concurrent fetches
      if (isFetchingRef.current) return;

      // First check if conversations data exists
      let conversationsData = queryClient.getQueryData<
        InfiniteData<ConversationListResponseDto>
      >(["conversations"]);

      // If data doesn't exist, fetch it first
      if (!conversationsData?.pages) {
        try {
          isFetchingRef.current = true;
          await queryClient.prefetchInfiniteQuery({
            queryKey: ["conversations"],
            queryFn: ({ pageParam = 1 }) =>
              chatApi.getConversations({
                page: pageParam as number,
                pageSize: 100,
              }),
            initialPageParam: 1,
          });

          // Get the data after prefetching
          conversationsData = queryClient.getQueryData<
            InfiniteData<ConversationListResponseDto>
          >(["conversations"]);
        } catch (error) {
          console.error("Failed to fetch conversations:", error);
          return;
        } finally {
          isFetchingRef.current = false;
        }
      }

      if (!conversationsData?.pages) {
        return;
      }

      const conversations = conversationsData.pages.flatMap(
        (page) => page.conversations || []
      );

      if (conversations.length === 0) {
        return;
      }

      // Only join rooms not already joined
      const newRooms = conversations.filter(
        (c) => !joinedRoomsRef.current.has(c.id)
      );

      if (newRooms.length === 0) {
        return;
      }

      newRooms.forEach((conversation) => {
        s.emit(ChatSubscribeMessage.JOIN_CONVERSATION, {
          conversationId: conversation.id,
        });
        joinedRoomsRef.current.add(conversation.id);
      });

      console.log(
        `Joined ${newRooms.length} new conversation rooms for global notifications`
      );
    },
    [queryClient]
  );

  // Debounced version for cache subscription to prevent spam
  const debouncedJoinRooms = useMemo(
    () => debounce((s: Socket) => joinAllConversationRooms(s), 300),
    [joinAllConversationRooms]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedJoinRooms.cancel();
    };
  }, [debouncedJoinRooms]);

  // Listen for session expiry to stop socket reconnection
  useEffect(() => {
    const handleSessionExpired = () => {
      console.warn("Session expired, stopping socket reconnection");
      stopSocketReconnection();
    };
    sessionEvents.addEventListener("session-expired", handleSessionExpired);
    return () => {
      sessionEvents.removeEventListener(
        "session-expired",
        handleSessionExpired
      );
    };
  }, []);

  useEffect(() => {
    const token = secureStorage.get(SecureStorageKeys.CUSTOM_TOKEN);
    if (!token) {
      console.warn("chat socket missing auth token");
      return;
    }

    const s = initChatSocket(token);

    const handleConnect = () => {
      setIsConnected(true);
      // Refetch message history to recover messages missed during disconnection
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
      // Join all conversation rooms to receive notifications (async)
      void joinAllConversationRooms(s);
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      // Clear joined rooms on disconnect so they re-join on reconnect
      joinedRoomsRef.current.clear();
    };

    s.on("connect", handleConnect);
    s.on("disconnect", handleDisconnect);

    setSocket(s);

    return () => {
      s.off("connect", handleConnect);
      s.off("disconnect", handleDisconnect);
      disconnectSocket();
    };
  }, [joinAllConversationRooms, queryClient]);

  // Listen for conversations query updates and join new rooms
  // Only trigger on fetch success, not on optimistic updates (setQueryData)
  useEffect(() => {
    if (!socket || !isConnected) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.query?.queryKey?.[0] === "conversations" &&
        event.type === "updated" &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event as any).action?.type === "success"
      ) {
        // When conversations data is fetched (not optimistic update), join new rooms
        debouncedJoinRooms(socket);
      }
    });

    return unsubscribe;
  }, [socket, isConnected, queryClient, debouncedJoinRooms]);

  const value = useMemo(() => ({ socket, isConnected }), [socket, isConnected]);

  return (
    <ChatSocketContext.Provider value={value}>
      {children}
    </ChatSocketContext.Provider>
  );
}
